// src/pages/Home.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Shield,
  Wallet,
  TrendingUp,
  Lock,
  Users,
  ArrowRight,
  CheckCircle,
  BarChart,
  Globe,
  Sparkles,
  ShieldCheck,
  Zap,
  Key,
  Cpu,
  Brain,
  AlertTriangle,
  Database,
  Network,
  Clock,
  FileText,
  Search,
  MessageSquare,
  UserCheck,
  Scale,
  Server,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";
import AOS from "aos";
import "aos/dist/aos.css";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

const Home: React.FC = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [fade, setFade] = useState(true);

  const heroTexts = [
    {
      main: "Intelligent Crypto Protection",
      highlight: "Powered by Advanced AI",
      color: "from-primary to-primary/80",
    },
    {
      main: "Expert Asset Recovery",
      highlight: "When Security Fails",
      color: "from-blue-500 to-blue-400",
    },
    {
      main: "Multi-Chain Security",
      highlight: "For Modern Portfolios",
      color: "from-green-500 to-green-400",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length);
        setFade(true);
      }, 1500);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 500,
      easing: "ease-in-out-quad",
      once: false,
    });
  }, []);

  const { ref: statsRef, inView: statsInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 overflow-hidden">
      {/* Navigation */}
      <PublicNavbar currentPage="/" />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Glow Effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 lg:px-5 py-20 lg:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div data-aos="fade-right" data-aos-delay="100">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm">
                <ShieldCheck className="h-4 w-4 mr-2" />
                Enterprise-Grade Crypto Security & Recovery
              </div>

              {/* Rotating Hero Text */}
              <div className="h-44 lg:h-56 mb-8">
                <div
                  className={`transition-all duration-700 ${
                    fade
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 -translate-y-4"
                  }`}
                >
                  <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 dark:text-white leading-tight">
                    {heroTexts[currentTextIndex].main}
                    <span
                      className={`block bg-gradient-to-r ${heroTexts[currentTextIndex].color} bg-clip-text text-transparent`}
                    >
                      {heroTexts[currentTextIndex].highlight}
                    </span>
                  </h1>
                </div>
              </div>

              <p
                className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-10 max-w-lg"
                data-aos="fade-right"
                data-aos-delay="200"
              >
                SyvronRecovery combines neural network-based market analysis
                with blockchain forensic expertise to proactively protect and
                systematically recover digital assets across all major networks.
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                <Link
                  to="/register"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} group/cta`}
                >
                  <span>Start Advanced Protection</span>
                  <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover/cta:translate-x-1" />
                </Link>
                <Link
                  to="/about"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonLarge} group/about`}
                >
                  <Sparkles className="h-5 w-5 mr-2 opacity-0 group-hover/about:opacity-100 transition-opacity" />
                  <span>Explore Our Technology</span>
                </Link>
              </div>
            </div>

            {/* Right Content - Enhanced Feature Cards */}
            <div className="relative" data-aos="fade-left" data-aos-delay="400">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>

              {/* Main Feature Card Container */}
              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 p-6 lg:p-8">
                <div className="space-y-6">
                  {/* AI Market Protection Card - Enhanced */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-r from-green-500/10 to-green-500/5 border border-green-500/20 group/feature hover:scale-102 transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="100"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-green-500/20 group-hover/feature:scale-110 transition-transform">
                        <Brain className="h-6 w-6 text-green-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            AI Market Protection
                          </h3>
                          <Zap className="h-4 w-4 text-green-500 opacity-0 group-hover/feature:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          Neural network algorithms analyze 50+ market
                          indicators to predict volatility and trigger automated
                          protection protocols before significant losses occur.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full border border-green-500/20">
                            Real-time Analysis
                          </span>
                          <span className="text-xs px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full border border-green-500/20">
                            Predictive Alerts
                          </span>
                          <span className="text-xs px-2 py-1 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full border border-green-500/20">
                            Auto-Protection
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expert Recovery Services Card - Enhanced */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-r from-blue-500/10 to-blue-500/5 border border-blue-500/20 group/feature hover:scale-102 transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-blue-500/20 group-hover/feature:scale-110 transition-transform">
                        <Shield className="h-6 w-6 text-blue-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            Expert Recovery Services
                          </h3>
                          <Key className="h-4 w-4 text-blue-500 opacity-0 group-hover/feature:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          Certified blockchain forensic team with 85% success
                          rate in recovering assets from sophisticated scams,
                          hacks, and lost wallet access across all major
                          networks.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full border border-blue-500/20">
                            Forensic Analysis
                          </span>
                          <span className="text-xs px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full border border-blue-500/20">
                            Legal Coordination
                          </span>
                          <span className="text-xs px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full border border-blue-500/20">
                            Smart Contract Recovery
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Multi-Chain Support Card - Enhanced */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-r from-purple-500/10 to-purple-500/5 border border-purple-500/20 group/feature hover:scale-102 transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-purple-500/20 group-hover/feature:scale-110 transition-transform">
                        <Network className="h-6 w-6 text-purple-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-bold text-gray-900 dark:text-white">
                            Multi-Chain Support
                          </h3>
                          <Globe className="h-4 w-4 text-purple-500 opacity-0 group-hover/feature:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          Comprehensive security and recovery solutions for 25+
                          blockchain networks including Bitcoin, Ethereum,
                          Solana, Polygon, BSC, Avalanche, and emerging L2
                          solutions.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs px-2 py-1 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-full border border-purple-500/20">
                            Bitcoin & Ethereum
                          </span>
                          <span className="text-xs px-2 py-1 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-full border border-purple-500/20">
                            Solana & Polygon
                          </span>
                          <span className="text-xs px-2 py-1 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-full border border-purple-500/20">
                            Layer 2 Networks
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="text-center mb-12 lg:mb-16" data-aos="fade-up">
            <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-4">
              Comprehensive Security Ecosystem
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Our integrated platform provides end-to-end protection from market
              analysis to forensic recovery
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* AI Protection Details */}
            <div
              className={`${GlobalStyles.card} backdrop-blur-xl group/feature`}
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-green-500/10 rounded-xl blur-lg"></div>
                <div className="relative w-14 h-14 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/feature:scale-110 transition-transform">
                  <Cpu className="h-7 w-7 text-green-500" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                AI-Driven Market Protection
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Our neural networks process terabytes of market data to identify
                patterns and predict volatility with 92% accuracy, triggering
                protection before significant losses.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  "Real-time sentiment analysis across 50+ sources",
                  "Pattern recognition for early volatility detection",
                  "Automated protection triggers with custom thresholds",
                  "Predictive alerts 24-48 hours before major moves",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-gray-200/30 dark:border-gray-700/30">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Clock className="h-4 w-4" />
                  <span>24/7 monitoring with 99.9% uptime</span>
                </div>
              </div>
            </div>

            {/* Recovery Services Details */}
            <div
              className={`${GlobalStyles.card} backdrop-blur-xl group/feature`}
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-blue-500/10 rounded-xl blur-lg"></div>
                <div className="relative w-14 h-14 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/feature:scale-110 transition-transform">
                  <Search className="h-7 w-7 text-blue-500" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Forensic Recovery Services
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Our certified forensic team specializes in blockchain analysis,
                legal coordination, and technical recovery across all major
                networks with proven success rates.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  "Blockchain transaction tracing and analysis",
                  "Smart contract vulnerability assessment",
                  "Legal coordination with international authorities",
                  "Multi-signature wallet recovery services",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-gray-200/30 dark:border-gray-700/30">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <FileText className="h-4 w-4" />
                  <span>85% successful recovery rate</span>
                </div>
              </div>
            </div>

            {/* Multi-Chain Support Details */}
            <div
              className={`${GlobalStyles.card} backdrop-blur-xl group/feature`}
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-purple-500/10 rounded-xl blur-lg"></div>
                <div className="relative w-14 h-14 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/feature:scale-110 transition-transform">
                  <Server className="h-7 w-7 text-purple-500" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Multi-Chain Security Infrastructure
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Unified security platform supporting 25+ blockchain networks
                with specialized protocols for each chain's unique architecture
                and security requirements.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  "Bitcoin/Ethereum legacy network expertise",
                  "Solana/Polygon high-speed chain optimization",
                  "Cross-chain bridging security protocols",
                  "Layer 2 solution implementation support",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-purple-500 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t border-gray-200/30 dark:border-gray-700/30">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Database className="h-4 w-4" />
                  <span>25+ supported blockchain networks</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-gray-50/50 to-transparent dark:from-gray-900/30">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="text-center mb-12 lg:mb-16" data-aos="fade-up">
            <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-4">
              Advanced Technology Stack
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Powered by cutting-edge technologies and security protocols
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                icon: Brain,
                title: "Neural Networks",
                desc: "Deep learning algorithms for market prediction",
                color: "text-green-500",
                bg: "from-green-500/10 to-green-500/5",
                border: "border-green-500/20",
              },
              {
                icon: Shield,
                title: "Zero-Knowledge",
                desc: "ZK proofs for private transaction verification",
                color: "text-blue-500",
                bg: "from-blue-500/10 to-blue-500/5",
                border: "border-blue-500/20",
              },
              {
                icon: Lock,
                title: "HSM Security",
                desc: "Hardware Security Module protection",
                color: "text-primary",
                bg: "from-primary/10 to-primary/5",
                border: "border-primary/20",
              },
              {
                icon: Network,
                title: "Multi-Chain",
                desc: "Cross-chain compatibility layer",
                color: "text-purple-500",
                bg: "from-purple-500/10 to-purple-500/5",
                border: "border-purple-500/20",
              },
            ].map((tech, idx) => (
              <div
                key={idx}
                className={`p-6 rounded-2xl bg-gradient-to-br ${tech.bg} border ${tech.border} text-center group/tech hover:scale-105 transition-all duration-300`}
                data-aos="zoom-in"
                data-aos-delay={idx * 100}
              >
                <div className="w-14 h-14 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center mx-auto mb-4 border border-gray-200/30 dark:border-gray-700/30 group-hover/tech:scale-110 transition-transform">
                  <tech.icon className={`h-7 w-7 ${tech.color}`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  {tech.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {tech.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Stats Section */}
      <section className="py-16 lg:py-20" ref={statsRef}>
        <div className="container mx-auto px-4 lg:px-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 rounded-3xl blur-xl opacity-50"></div>
            <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8 lg:p-12">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {[
                  {
                    value: 92,
                    suffix: "%",
                    label: "Market Prediction Accuracy",
                    desc: "AI algorithm success rate",
                    color: "text-green-500",
                    icon: Brain,
                    delay: 100,
                  },
                  {
                    value: 42,
                    prefix: "$",
                    suffix: "M+",
                    label: "Assets Recovered",
                    desc: "Total value retrieved",
                    color: "text-blue-500",
                    icon: Shield,
                    delay: 200,
                  },
                  {
                    value: 25,
                    suffix: "+",
                    label: "Blockchain Networks",
                    desc: "Supported chains",
                    color: "text-purple-500",
                    icon: Network,
                    delay: 300,
                  },
                  {
                    value: 5000,
                    suffix: "+",
                    label: "Protected Portfolios",
                    desc: "Active security coverage",
                    color: "text-primary",
                    icon: UserCheck,
                    delay: 400,
                  },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="text-center group/stat"
                    data-aos="zoom-in"
                    data-aos-delay={stat.delay}
                  >
                    <div className="flex justify-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-700 rounded-xl flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/stat:scale-110 transition-transform">
                        <stat.icon className={`h-6 w-6 ${stat.color}`} />
                      </div>
                    </div>
                    <div
                      className={`text-3xl lg:text-4xl font-bold ${stat.color} mb-2 group-hover/stat:scale-105 transition-transform`}
                    >
                      {statsInView && (
                        <CountUp
                          end={stat.value}
                          prefix={stat.prefix}
                          suffix={stat.suffix}
                          duration={2.5}
                        />
                      )}
                    </div>
                    <div className="text-sm lg:text-base font-medium text-gray-900 dark:text-white mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {stat.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Standards Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div data-aos="fade-right">
              <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent mb-6">
                Enterprise-Grade Security Standards
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                We maintain the highest security certifications and follow
                rigorous protocols to ensure your assets remain protected at all
                times.
              </p>
              <div className="space-y-4">
                {[
                  "SOC 2 Type II Certified Security Operations",
                  "ISO 27001 Information Security Management",
                  "AES-256 Encryption with Hardware Security Modules",
                  "Regular Third-Party Security Audits",
                  "Multi-Signature Wallet Implementation",
                  "Zero-Knowledge Proof Verification",
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative" data-aos="fade-left">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                      <Scale className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white">
                        Legal Compliance
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Full regulatory compliance across 50+ jurisdictions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                      <MessageSquare className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white">
                        24/7 Support
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Dedicated security team with average 2-minute response
                        time
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="h-6 w-6 text-purple-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 dark:text-white">
                        Risk Monitoring
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Continuous threat detection and proactive risk
                        mitigation
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div
              className="relative bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8 lg:p-12 text-center backdrop-blur-sm border border-primary/30"
              data-aos="fade-up"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Start Protecting Your Digital Assets Today
              </h2>
              <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
                Join thousands of investors who trust SyvronRecovery's advanced
                AI protection and expert recovery services for comprehensive
                crypto security.
              </p>
              <div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <Link
                  to="/register"
                  className="bg-white text-primary hover:bg-gray-100 px-6 lg:px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center justify-center group/cta"
                >
                  <span>Get Advanced Protection</span>
                  <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover/cta:translate-x-1" />
                </Link>
                <Link
                  to="/about"
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-6 lg:px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105"
                >
                  <Sparkles className="h-5 w-5 mr-2 inline-block" />
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="SyvronRecovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  SyvronRecovery
                </span>
              </div>
              <p className="mb-4 text-sm">
                Enterprise-grade cryptocurrency protection and recovery services
                powered by advanced AI and blockchain forensic expertise.
              </p>
              <div className="flex space-x-3">
                <Globe className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
                <Users className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
                <Lock className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/wallet"
                    className="hover:text-white transition-colors"
                  >
                    AI Market Protection
                  </Link>
                </li>
                <li>
                  <Link
                    to="/recovery"
                    className="hover:text-white transition-colors"
                  >
                    Forensic Recovery
                  </Link>
                </li>
                <li>
                  <Link
                    to="/dashboard"
                    className="hover:text-white transition-colors"
                  >
                    Portfolio Dashboard
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="hover:text-white transition-colors"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2 text-sm">security@syvronrecovery.com</p>
              <p className="text-sm">24/7 Expert Security Support</p>
              <p className="text-sm mt-2">
                Emergency Recovery Hotline: +1-800-CRYPT-SEC
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>
              © {new Date().getFullYear()} SyvronRecovery. All rights reserved.
              SOC 2 Type II & ISO 27001 Certified.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
